import UploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

interface ModalAttachmentProps {
	isOpen: boolean;
	onClose: () => void;
	onAttach: (file: File) => void;
	onRemove: () => void;
	existingFileName?: string | null;
	nameDocument?: string;
}

export const normalizeFileName = (fileName: string): string => {
	return fileName
		.normalize("NFD")
		.replace(/\u0300-\u036f/g, "")
		.replace(/\s+/g, "_")
		.replace(/[^a-zA-Z0-9._]/g, "");
};

export const ModalAttachment: React.FC<ModalAttachmentProps> = ({ isOpen, onClose, onAttach, onRemove, existingFileName, nameDocument }) => {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [error, setError] = useState<string | null>(null);
	const supportedFormats = ["png", "jpg", "jpeg", "doc", "docx", "pdf", "odt"];

	useEffect(() => {
		if (existingFileName && isOpen) {
			setSelectedFile(new File([], existingFileName));
		} else if (!isOpen) {
			setSelectedFile(null);
			setError(null);
		}
	}, [existingFileName, isOpen]);

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			const fileExtension = file.name.split(".").pop()?.toLowerCase();

			if (!fileExtension || !supportedFormats.includes(fileExtension)) {
				setError(`Formato inválido. Permitidos: ${supportedFormats.join(", ").toUpperCase()}`);
				setSelectedFile(null);
			} else {
				const normalized = normalizeFileName(file.name);
				const normalizedFile = new File([file], normalized, { type: file.type });
				setSelectedFile(normalizedFile);
				setError(null);
			}
		}
	};

	const handleAttach = () => {
		if (selectedFile) {
			onAttach(selectedFile);
			handleClose();
		}
	};

	const handleRemoveFile = () => {
		setSelectedFile(null);
		onRemove();
	};

	const formatFileSize = (size: number) => {
		return size < 1024 ? `${size}B` : size < 1024 * 1024 ? `${(size / 1024).toFixed(2)}KB` : `${(size / (1024 * 1024)).toFixed(2)}MB`;
	};

	const handleClose = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}

		setTimeout(() => {
			onClose();
		}, 0);
	};

	return (
		<Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
			<DialogTitle>Anexar {nameDocument}</DialogTitle>
			<DialogContent>
				{error && (
					<Typography
						variant="body2"
						sx={{
							color: "#e36b5b",
							bgcolor: "#fcedea",
							p: 1,
							borderRadius: 2,
							mb: 2,
						}}
					>
						{error}
					</Typography>
				)}

				{selectedFile ? (
					<Box
						sx={{
							border: "2px dashed #FFD700",
							borderRadius: 2,
							p: 4,
							mt: 1,
							textAlign: "center",
						}}
					>
						<Typography variant="body1">{selectedFile.name}</Typography>
						<Typography variant="body2" color="text.secondary">
							Tamanho: {selectedFile.size > 0 ? formatFileSize(selectedFile.size) : "Desconhecido"}
						</Typography>
						<Button color="error" startIcon={<DeleteIcon />} onClick={handleRemoveFile}>
							Apagar
						</Button>
					</Box>
				) : (
					<Box
						sx={{
							border: "2px dashed #FFD700",
							borderRadius: 2,
							p: 4,
							mt: 1,
							cursor: "pointer",
							textAlign: "center",
						}}
						onClick={() => document.getElementById("upload-input")?.click()}
					>
						<UploadIcon sx={{ fontSize: 48, color: "#666", mb: 1 }} />
						<Typography variant="body2" color="text.secondary">
							Clique para carregar o arquivo (máx. 50MB)
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Tipos: PNG, JPG, JPEG, DOC, DOCX, PDF, ODT
						</Typography>
					</Box>
				)}

				<input id="upload-input" type="file" style={{ display: "none" }} onChange={handleFileChange} />
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose}>Cancelar</Button>
				<Button onClick={handleAttach} disabled={!selectedFile || !!error} variant="contained">
					Anexar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
